/**
 * =============================================================================
 * Componente: ReferralReferralsTableView
 * Versão: 1.6.0
 *
 * Descrição:
 *   Tabela administrativa de **Indicações** (leads/funil) seguindo o padrão
 *   visual das demais listagens do módulo (Afiliados, Comissões):
 *     - Tabela full-bleed (sem moldura externa).
 *     - Barra lateral colorida por status no início da linha.
 *     - Headers compactos.
 *     - Badges densos `text-[10px]`.
 *     - Skeleton e estado vazio também full-bleed.
 *
 *   Colunas (v1.2.0):
 *     Lead → Telefone → Programa → Campanha → Afiliado → Status (Indicação)
 *     → Valor → Categoria Negócio → Fase Negócio → Status Negócio
 *     → Vendedor → Nº Negócio → Nº Contato → Criado em → Convertido em → Ações
 *
 * RELEASE:
 *   2026-04-26 v1.0.0 - Criação inicial.
 *   2026-04-26 v1.1.0 - Desmembrada coluna "Origem" em 3 colunas independentes
 *                       (Programa, Campanha, Afiliado) para melhor leitura.
 *   2026-04-26 v1.2.0 - Adicionadas 5 colunas: Telefone do lead + dados do
 *                       negócio vinculado (Categoria, Fase, Status, Vendedor).
 *                       Os dados do negócio dependem da RPC v1.1.0.
 *   2026-04-28 v1.3.0 - Célula "Nº Negócio" agora é clicável e dispara
 *                       `onOpenDeal(deal_id)` para abrir o modal premium do
 *                       negócio vinculado à indicação.
 *   2026-04-28 v1.4.0 - Coluna "Fase" passa a usar o componente
 *                       <DealPhaseProgress /> (barra de progresso colorida)
 *                       reaproveitado da listagem de Negócios. Badge
 *                       "Pendente" migrado de amarelo para âmbar.
 *                       Depende da RPC get_referrals_paginated v1.2.0.
 *   2026-04-29 v1.5.0 - Padronização visual 1:1 com CommissionsTableView e
 *                       PayoutsTableView: tabela `table-fixed` com <colgroup>
 *                       (larguras fixas), wrapper flex full-bleed,
 *                       header sticky com bg-muted/20, padding compacto
 *                       `px-2 py-1.5` em todas as cells e bordas
 *                       `border-b border-border/40` nas linhas. Garante que
 *                       as 3 listagens do módulo tenham linhas com
 *                       altura/largura idênticas.
 *   2026-04-30 v1.6.0 - Adicionada coluna "Ref." (UUID curto, 8 chars) como 1ª
 *                       coluna depois da barra de status, em paridade com
 *                       AffiliatesTableView, CommissionsTableView e
 *                       PayoutsTableView. Sem tooltip (não expõe UUID
 *                       completo no DOM).
 *   2026-04-30 v1.7.0 - Coluna "Ref." passa a ser clicável: dispara
 *                       `onOpenDetails(id)` para abrir o sheet de detalhes
 *                       da indicação (paridade com a UX de Comissões/Pagamentos).
 * =============================================================================
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Inbox,
  Eye,
  ExternalLink,
  RotateCcw,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
// Badge removido — coluna Status agora usa <ReferralStatusBadge /> (dot + texto)
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';
import { StatusBar } from '@/components/referral/shared/StatusBar';
// Reaproveita a barra de progresso da fase usada na listagem de Negócios (CRM)
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
// v1.8.0 (2026-05-02) — Badge padronizado de status do negócio
// (substitui o mapa local DEAL_STATUS_CONFIG por util compartilhado).
import { DealStatusBadge } from '@/components/crm/DealStatusBadge';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
// v1.9.0 (2026-05-02) — Status da indicação padronizado via util compartilhado
// (dot colorido + texto, mesmo padrão do painel do afiliado).
import {
  ReferralStatusBadge,
  REFERRAL_STATUS_CONFIG,
} from '@/components/referral/ReferralStatusBadge';
import type {
  AdminReferralRow,
  ReferralAdminStatus,
} from '@/hooks/useReferralReferralsAdmin';

// ============================================================================
// CONFIG VISUAL
// ============================================================================

// v1.9.0 (2026-05-02) — STATUS_CONFIG local removido. Status da indicação
// agora vem do util compartilhado <ReferralStatusBadge /> + REFERRAL_STATUS_CONFIG
// (dot colorido + texto, mesmo padrão do painel do afiliado). A barra lateral
// da linha (`StatusBar`) reaproveita REFERRAL_STATUS_CONFIG[status].bar.

// v1.8.0 — DEAL_STATUS_CONFIG local removido. Status do negócio agora é
// renderizado pelo util compartilhado <DealStatusBadge /> (soft tinted,
// dark-mode aware), garantindo padronização com as demais telas do CRM.

/** Formatação BRL. */
function formatBRL(value: number | null | undefined) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/**
 * Formatação de telefone BR.
 * Aceita strings em qualquer formato; tenta exibir como (DDD) NNNNN-NNNN.
 */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '—';
  const digits = String(raw).replace(/\D+/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

/** Truncamento de UUID para 8 chars (padrão do projeto). */
function shortId(id: string | null | undefined) {
  if (!id) return '—';
  return id.slice(0, 8).toUpperCase();
}

// ============================================================================
// PROPS
// ============================================================================

interface ReferralReferralsTableViewProps {
  referrals: AdminReferralRow[];
  isLoading: boolean;
  onOpenDetails: (id: string) => void;
  onReprocess?: (id: string) => void;
  reprocessingId?: string | null;
  /**
   * Callback opcional para abrir o modal de detalhes do negócio vinculado
   * (clique na célula "Nº Negócio"). Se não for fornecido, a célula
   * permanece como exibição estática (somente leitura).
   */
  onOpenDeal?: (dealId: string) => void;
}

// ============================================================================
// COMPONENTE
// ============================================================================

export function ReferralReferralsTableView({
  referrals,
  isLoading,
  onOpenDetails,
  onReprocess,
  reprocessingId,
  onOpenDeal,
}: ReferralReferralsTableViewProps) {
  // Skeleton durante carregamento inicial
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // Estado vazio
  if (!referrals.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Inbox className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm font-medium">Nenhuma indicação encontrada</p>
        <p className="text-xs text-muted-foreground mt-1">
          Quando um afiliado capturar um lead, ele aparecerá aqui.
        </p>
      </div>
    );
  }

  // ========================================================================
  // RENDER (v1.5.0)
  // ----------------------------------------------------------------------
  // Chassis 1:1 com `CommissionsTableView` e `PayoutsTableView`:
  //   - Wrapper flex full-bleed com overflow-auto interno.
  //   - Tabela `table-fixed` + <colgroup> com larguras fixas → garante que
  //     todas as linhas tenham EXATAMENTE a mesma altura/largura nas três
  //     listagens do módulo (Indicações, Comissões, Pagamentos).
  //   - Header sticky com bg-muted/20 (paridade visual).
  //   - Padding compacto px-2 py-1.5 nas cells.
  // ========================================================================
  return (
    <ScrollIndicators className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-full">
      <div data-scroll-viewport className="flex-1 min-h-0 w-full max-w-full overflow-auto">
        <table
          className="w-max caption-bottom text-sm table-fixed"
          style={{ minWidth: '1975px' }}
        >
          {/* Larguras fixas das colunas — totaliza ~1975px (v1.6.0: + col Ref.) */}
          <colgroup>
            <col style={{ width: 8 }} />   {/* Barra de status */}
            <col className="w-[75px] " />  {/* Ref. (v1.6.0) */}
            <col className="w-[180px]" />  {/* Lead */}
            <col className="w-[130px]" />  {/* Telefone */}
            <col className="w-[140px]" />  {/* Programa */}
            <col className="w-[140px]" />  {/* Campanha */}
            <col className="w-[170px]" />  {/* Afiliado */}
            <col className="w-[100px]" />  {/* Valor */}
            <col className="w-[95px]" />   {/* Status (movido p/ logo após Valor) */}
            <col className="w-[130px]" />  {/* Categoria Negócio */}
            <col className="w-[160px]" />  {/* Fase Negócio */}
            <col className="w-[95px]" />   {/* Status Negócio */}
            <col className="w-[150px]" />  {/* Vendedor */}
            <col className="w-[90px]" />   {/* Nº Negócio */}
            <col className="w-[80px]" />   {/* Nº Contato */}
            <col className="w-[110px]" />  {/* Criado em */}
            <col className="w-[110px]" />  {/* Convertido em */}
            <col className="w-[70px]" />   {/* Ações */}
          </colgroup>

          {/* Header sticky padronizado */}
          <TableHeader className="sticky top-0 bg-background z-[1]">
            <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
              <TableHead className="w-2 p-0" />
              <TableHead className="text-xs font-medium px-2 py-1.5">Ref.</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Lead</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Telefone</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Programa</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Campanha</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Afiliado</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5 text-right">Valor</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Status</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Categoria Negócio</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Fase Negócio</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Status Negócio</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Vendedor</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Nº Negócio</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Nº Contato</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Criado em</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">Convertido em</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
        <TableBody>
          {referrals.map((r) => {
            // Reaproveita as cores do util compartilhado para a barra lateral da linha.
            const cfg =
              REFERRAL_STATUS_CONFIG[r.status] ?? REFERRAL_STATUS_CONFIG.pending;
            const hasError = !!r.metadata?.crm_processing_error;
            const reprocessing = reprocessingId === r.id;

            // v1.8.0 — status do negócio é renderizado pelo util compartilhado
            // <DealStatusBadge /> diretamente na célula (sem mapa local).

            return (
              <TableRow
                key={r.id}
                className="hover:bg-muted/40 border-b border-border/40 group transition-colors"
              >
                {/* Barra lateral colorida por status (helper compartilhado) */}
                <TableCell className="p-0 w-2">
                  <StatusBar colorClass={cfg.bar} />
                </TableCell>

                {/* Ref. — UUID curto clicável (v1.7.0) → abre o sheet de detalhes */}
                <TableCell className="px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => onOpenDetails(r.id)}
                    className="font-mono text-[11px] text-primary hover:underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                    title="Abrir detalhes da indicação"
                  >
                    {shortId(r.id)}
                  </button>
                </TableCell>

                {/* Lead */}
                <TableCell className="px-2 py-1.5 text-sm">
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">
                      {r.lead_name || '—'}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {r.lead_email || '—'}
                    </span>
                  </div>
                </TableCell>

                {/* Telefone (coluna dedicada) */}
                <TableCell className="px-2 py-1.5 text-xs whitespace-nowrap">
                  {r.lead_phone ? (
                    <span className="font-mono">{formatPhone(r.lead_phone)}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Programa */}
                <TableCell className="px-2 py-1.5 text-xs">
                  <span className="font-medium truncate block max-w-[160px]">
                    {r.program_name || '—'}
                  </span>
                </TableCell>

                {/* Campanha */}
                <TableCell className="px-2 py-1.5 text-xs">
                  <span className="text-muted-foreground truncate block max-w-[160px]">
                    {r.campaign_name || '—'}
                  </span>
                </TableCell>

                {/* Afiliado */}
                <TableCell className="px-2 py-1.5 text-xs">
                  <div className="flex flex-col min-w-0 max-w-[180px]">
                    <span className="font-medium truncate">
                      {r.affiliate_name || '—'}
                    </span>
                    {r.affiliate_email && (
                      <span className="text-muted-foreground truncate text-[11px]">
                        {r.affiliate_email}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Valor */}
                <TableCell className="px-2 py-1.5 text-sm text-right font-medium">
                  {formatBRL(r.conversion_value)}
                </TableCell>

                {/* Status (Indicação) — movido p/ logo após Valor */}
                <TableCell className="px-2 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <ReferralStatusBadge status={r.status} />
                    {hasError && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-xs">
                            Erro no processamento CRM:{' '}
                            <strong>
                              {String(r.metadata?.crm_processing_error)}
                            </strong>
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>

                {/* Categoria do Negócio */}
                <TableCell className="px-2 py-1.5 text-xs">
                  {r.deal_category_name ? (
                    <span className="truncate block max-w-[140px]">
                      {r.deal_category_name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Fase do Negócio — barra de progresso reaproveitada do CRM */}
                <TableCell className="px-2 py-1.5 text-xs">
                  {r.deal_phase_name ? (
                    <DealPhaseProgress
                      phaseName={r.deal_phase_name}
                      phaseColor={r.deal_phase_color}
                      phaseSortOrder={r.deal_phase_sort_order ?? 1}
                      totalPhases={r.deal_pipeline_total_phases ?? 1}
                    />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Status do Negócio — v1.8.1: guard p/ deal_status nulo (indicação sem deal) */}
                <TableCell className="px-2 py-1.5">
                  {r.deal_status ? (
                    <DealStatusBadge status={r.deal_status} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Vendedor (avatar com fallback 3D + nome) */}
                <TableCell className="px-2 py-1.5 text-xs">
                  {r.deal_seller_name ? (
                    <div className="flex items-center gap-2 min-w-0 max-w-[160px]">
                      <SmartAvatar
                        src={r.deal_seller_avatar}
                        name={r.deal_seller_name}
                        size="xs"
                        use3DFallback
                      />
                      <span className="truncate">{r.deal_seller_name}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Nº Negócio — clicável quando onOpenDeal está disponível */}
                <TableCell className="px-2 py-1.5 text-xs font-mono">
                  {r.deal_id ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {onOpenDeal ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              // Evita disparar handlers de linha (ex.: abrir detalhes da indicação)
                              e.stopPropagation();
                              onOpenDeal(r.deal_id!);
                            }}
                            className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                          >
                            {shortId(r.deal_id)}
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-primary cursor-help">
                            {shortId(r.deal_id)}
                            <ExternalLink className="h-3 w-3" />
                          </span>
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">{r.deal_title || 'Negócio'}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Nº Contato */}
                <TableCell className="px-2 py-1.5 text-xs font-mono">
                  {r.contact_id ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">{shortId(r.contact_id)}</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">{r.contact_name || 'Contato'}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Criado em */}
                <TableCell className="px-2 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                  {format(new Date(r.created_at), 'dd/MM/yyyy HH:mm', {
                    locale: ptBR,
                  })}
                </TableCell>

                {/* Convertido em */}
                <TableCell className="px-2 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                  {r.converted_at
                    ? format(new Date(r.converted_at), 'dd/MM/yyyy HH:mm', {
                        locale: ptBR,
                      })
                    : '—'}
                </TableCell>

                {/* Ações */}
                <TableCell className="px-2 py-1.5 text-right pr-4">
                  <div className="flex items-center justify-end gap-1">
                    {onReprocess && (!r.contact_id || !r.deal_id || hasError) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => onReprocess(r.id)}
                            disabled={reprocessing}
                          >
                            {reprocessing ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Reprocessar (criar contato/negócio)</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onOpenDetails(r.id)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        </table>
      </div>
    </ScrollIndicators>
  );
}
